import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import fetchTeamStatsAggregated from "hooks/getTeamStatsAggregated";
import { filterStatsByHistoryRange } from "../../utils/teamHistoryFilter";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import appConfig from "appConfig";
import "assets/styles.css";
const SeparatorSvg = `/icons/SeparatorContainer_S1.svg`;
const ArrowUpSvg = `/icons/arrow_upward_alt_S1.svg`;
const ArrowDownSvg = `/icons/arrow_downward_alt_2.svg`;

export default function MatchesPlayTable({ teamName, gender, historyRange }) {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isAscending, setIsAscending] = useState(false);
  // const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { instance, accounts } = useMsal(); // MSAL instance for token handling

  const sortData = (data) => {
    const sorted = [...data].sort((a, b) => {
      if (isAscending) {
        return a.year - b.year;
      } else {
        return b.year - a.year;
      }
    });
    setFilteredData(sorted);
  };

  useEffect(() => {
    const loadDataAsync = async () => {
      try {
        // setLoading(true);
        setError(null);

        if (!accounts || accounts.length === 0) {
          throw new Error("No account found");
        }

        // Token acquisition request
        const request = {
          scopes: appConfig.authScopes,

          account: accounts[0],
        };

        // Acquire token silently
        const accessTokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = accessTokenResponse.accessToken;

        const teamEntry = teamCodeHashmap[teamName.toLowerCase()];
        const teamCode = teamEntry ? Object.keys(teamEntry)[0] : "";

        // Fetch team stats aggregated with accessToken
        const data = await fetchTeamStatsAggregated(
          teamCode,
          gender,
          accessToken
        );
        setAllData(data);
        sortData(filterStatsByHistoryRange(data, historyRange));
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            scopes: appConfig.authScopes,
            account: accounts[0],
          });
        } else {
          setError(error.message);
        }
      }
    };

    loadDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, teamName]);

  useEffect(() => {
    sortData(filterStatsByHistoryRange(allData, historyRange));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, historyRange, isAscending]);

  const toggleSortOrder = () => {
    setIsAscending(!isAscending);
    sortData(filteredData);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      toggleSortOrder();
    }
  };

  // if (loading) {
  //   return (
  //     <div style={{ textAlign: 'left' }}>
  //       <div style={{ fontFamily: 'Biotif', marginBottom: '10px' }}>Loading...</div>
  //       <LinearProgress style={{ backgroundColor: 'rgb(39, 50, 212)' }} />
  //     </div>
  //   );
  // }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!filteredData.length) {
    return (
      <div className="TDChartEmptyState">
        No data available for selected range
      </div>
    );
  }

  return (
    <div>
      <TableContainer>
        <Table className={"tableWR"} aria-label="WR Table">
          <TableHead>
            <TableRow className={"TableHeadWR"}>
              <TableCell className={"yearContainerTDL"}>
                <div className={"typographyFont"}>Year</div>
                <div
                  className={"ArrowUp"}
                  onClick={toggleSortOrder}
                  onKeyDown={handleKeyPress}
                  role="button"
                  tabIndex="0"
                  style={{ cursor: "pointer" }}
                >
                  <img className="ExxpandAll" src={isAscending ? ArrowDownSvg : ArrowUpSvg} alt="Arrow Up" />
                </div>
                <div className={"separator"}>
                  <img
                    className="ExxpandAll"
                    src={SeparatorSvg}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"matchesPlayedContainerTDL"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Matches Played</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ExxpandAll"
                    src={SeparatorSvg}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"wonLostContainer"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Won/Lost</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ExxpandAll"
                    src={SeparatorSvg}
                    alt="Separator"
                  />
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, index) => (
              <TableRow key={index} className={"MatchesPlayedRowBody"}>
                <TableCell className={"YearOuterCell"}>
                  <div>{row.year}</div>
                </TableCell>
                <TableCell className={"MatchesPlayedOuterCell"}>
                  <div>{row.match_played}</div>
                </TableCell>
                <TableCell className={"WonLostOuterCell"}>
                  <Box
                    className={"WonOuterCell"}
                    sx={{
                      width: `${row.perc_win}%`,
                      minWidth: row.perc_win === 0 ? "2.786vw" : "auto",
                    }}
                  >
                    <Box className={"WonNumbCell"}>
                      <div className="WonNumbCellTypo">{row.win}</div>
                      <div className="WonPercCellTypo">|</div>
                      <div className="WonPercCellTypo">{`${row.perc_win}%`}</div>
                    </Box>
                  </Box>
                  <Box
                    className={"LostOuterCell"}
                    sx={{
                      width: `${row.perc_lose}%`,
                      minWidth: row.perc_lose === 0 ? "2.786vw" : "auto",
                    }}
                  >
                    <Box className={"WonNumbCell"}>
                      <div className="LostNumbCellTypo">{row.lose}</div>
                      <div className="WonPercCellTypo">|</div>
                      <div className="WonPercCellTypo">{`${row.perc_lose}%`}</div>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

MatchesPlayTable.propTypes = {
  teamName: PropTypes.string.isRequired,
  gender: PropTypes.number.isRequired,
  historyRange: PropTypes.shape({
    startYear: PropTypes.number,
    endYear: PropTypes.number,
    endDate: PropTypes.instanceOf(Date),
  }),
};

MatchesPlayTable.defaultProps = {
  historyRange: null,
};
